import { type LinkProps, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import type { SearchNavigate } from "./use-list-page";

type Tabs = readonly [string, ...string[]];

/**
 * The search schema of a page that holds several list pages as tabs
 * (Zone Access: codes | rules). `tab` picks the panel (absent = the first);
 * every other param passes through untouched and belongs to the panel, which
 * parses it with its own schema (`usePanelSearch`). Switching tab drops them.
 */
export const tabbedSearch = <const T extends Tabs>(tabs: T) => z.object({ tab: z.enum(tabs).optional().catch(undefined) }).passthrough();

/**
 * A panel's view of the URL: the host route's search parsed with the panel's
 * own lenient list schema, and a navigate that writes back to the same page
 * keeping `tab`. Satisfies `useListPage`'s `search` / `navigate`.
 */
export function usePanelSearch<S extends z.ZodTypeAny>(schema: S, to: NonNullable<LinkProps["to"]>) {
	const raw = useSearch({ strict: false }) as Record<string, unknown>;
	const nav = useNavigate();
	const search = useMemo(() => schema.parse(raw) as z.infer<S>, [schema, raw]);
	const navigate: SearchNavigate<z.infer<S>> = useCallback(
		(opts) =>
			nav({
				to,
				search: (prev: Record<string, unknown>) => ({ ...opts.search(schema.parse(prev)), tab: prev.tab }),
				replace: opts.replace,
			} as never),
		[nav, schema, to],
	);
	return { search, navigate };
}
